import { useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";
import type { Project, Workspace } from "@racler/protocol";
import { Header } from "../components/Header.tsx";
import { Ide } from "../components/IdeLayout.tsx";
import { PanelControls } from "../components/PanelControls.tsx";
import type { User } from "../lib/api.ts";
import { useRelay } from "../lib/relayContext.tsx";
import { navigate, projectPath, setNavigationGuard } from "../lib/router.ts";
import { shouldConfirmLeave } from "../lib/projectContext.ts";
import { removeRecent, touchRecent } from "../lib/recent.ts";
import { useIdeLayout } from "../lib/useIdeLayout.ts";
import { useMachines } from "../lib/useMachines.ts";
import { useOpenFiles } from "../lib/useOpenFiles.ts";
import { LspProvider, useProjectLsp } from "../lib/lsp/useProjectLsp.ts";

import {
  ADD_PROJECT_OPTION,
  filterProjectsByWorkspace,
  projectBelongsToWorkspace,
  resolveActiveWorkspaceId,
  targetUrlForProjectChange,
  targetUrlForWorkspaceChange,
} from "../lib/projectHeader.ts";

interface Props {
  user: User;
  machineId: string;
  projectId?: string | undefined;
  chatId?: string | undefined;
  onLogout: () => void;
}

export function ProjectPage({ user, machineId, projectId, chatId, onLogout }: Props) {
  const relay = useRelay();
  const { machines } = useMachines();
  const machine = machines?.find((m) => m.id === machineId);
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[] | null>(null);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const online = machine?.online ?? false;
  const layout = useIdeLayout();

  // Subscribe to this machine's events (ownership is checked server-side) and load its projects.
  useEffect(() => {
    if (!online) return;
    let cancelled = false;
    const load = () => {
      relay
        .server("system/machine.subscribe", { machineId })
        .then(() =>
          Promise.all([
            relay.machine(machineId, "system/projects.list", {}),
            relay.machine(machineId, "system/workspaces.list", {}),
          ]),
        )
        .then(
          ([p, w]) => !cancelled && (setProjects(p.projects), setWorkspaces(w.workspaces), setError(null)),
        )
        .catch((e: unknown) => !cancelled && setError((e as Error).message));
    };
    load();
    const off = relay.onReconnect(load);
    return () => {
      cancelled = true;
      off();
      relay.server("system/machine.unsubscribe", { machineId }).catch(() => undefined);
    };
  }, [relay, machineId, online]);

  const project = projects?.find((p) => p.id === projectId);
  const workspaceName = (id: string) => workspaces?.find((w) => w.id === id)?.name;

  const activeWorkspaceId = resolveActiveWorkspaceId({
    workspaces,
    projects,
    projectId,
    selectedWorkspaceId,
  });

  const filteredProjects = filterProjectsByWorkspace(projects, activeWorkspaceId);
  const hasWorkspaces = Boolean(workspaces && workspaces.length > 0);
  const isProjectValidForWorkspace = Boolean(project && projectBelongsToWorkspace(project, activeWorkspaceId));

  // Remember opened projects for the Home page ("Continue", "Recent projects").
  useEffect(() => {
    if (project) {
      const selectedWs = workspaces?.find((w) => w.id === project.workspaceId);
      touchRecent({
        machineId,
        projectId: project.id,
        name: project.name,
        workspaceName: selectedWs?.name,
        path: project.path,
      });
    }
  }, [machineId, project, workspaces]);

  useEffect(() => {
    if (projects && !project && projectId) {
      removeRecent(machineId, projectId);
    }
  }, [machineId, projectId, projects, project]);

  const onMachineChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextMachineId = e.target.value;
    if (!nextMachineId || nextMachineId === machineId) return;
    const ok = navigate(`/m/${nextMachineId}`);
    if (!ok) {
      e.target.value = machineId;
    }
  };

  const onWorkspaceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextWsId = e.target.value;
    if (!nextWsId || nextWsId === activeWorkspaceId) return;
    const target = targetUrlForWorkspaceChange(machineId, nextWsId, project);
    if (!target) {
      setSelectedWorkspaceId(nextWsId);
      return;
    }
    const ok = navigate(target);
    if (ok) {
      setSelectedWorkspaceId(nextWsId);
    } else {
      e.target.value = activeWorkspaceId ?? "";
    }
  };

  const onProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextPrjId = e.target.value;
    if (!nextPrjId || nextPrjId === projectId) return;
    const target = targetUrlForProjectChange(machineId, nextPrjId);
    const ok = navigate(target);
    if (!ok) {
      e.target.value = isProjectValidForWorkspace ? (projectId ?? "") : "";
    }
  };

  const context = (
    <span className="crumbs" data-testid="crumbs">
      <select
        className="crumb-select"
        aria-label="Machine"
        value={machineId}
        onChange={onMachineChange}
        data-testid="machine-select"
      >
        {(machines ?? []).map((m) => (
          <option key={m.id} value={m.id} disabled={!m.online && m.id !== machineId}>
            {m.name}
            {m.online ? "" : " (offline)"}
          </option>
        ))}
        {!machine && <option value={machineId}>Machine</option>}
      </select>
      {machine && <span className={`dot ${online ? "on" : "off"}`} title={online ? "Online" : "Offline"} />}

      <ChevronRight size={13} aria-hidden />

      <select
        className="crumb-select"
        aria-label="Workspace"
        value={activeWorkspaceId ?? ""}
        onChange={onWorkspaceChange}
        disabled={!online || !workspaces || workspaces.length === 0}
        data-testid="workspace-select"
      >
        {!online ? (
          <option value="" disabled>
            —
          </option>
        ) : !workspaces ? (
          <option value="" disabled>
            Loading…
          </option>
        ) : workspaces.length === 0 ? (
          <option value="" disabled>
            No Workspaces
          </option>
        ) : (
          <>
            <option value="" disabled>
              Choose Workspace…
            </option>
            {workspaces.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </>
        )}
      </select>

      <ChevronRight size={13} aria-hidden />

      <select
        className="crumb-select"
        aria-label="Project"
        value={isProjectValidForWorkspace ? (projectId ?? "") : ""}
        onChange={onProjectChange}
        disabled={!online || !hasWorkspaces}
        data-testid="project-select"
      >
        {!online || !hasWorkspaces ? (
          <option value="" disabled>
            —
          </option>
        ) : !projects ? (
          <option value="" disabled>
            Loading…
          </option>
        ) : filteredProjects.length === 0 ? (
          <>
            <option value="" disabled>
              No Projects
            </option>
            <option value={ADD_PROJECT_OPTION}>＋ Create or add Project…</option>
          </>
        ) : (
          <>
            <option value="" disabled>
              Choose Project…
            </option>
            {filteredProjects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
            <option value={ADD_PROJECT_OPTION}>＋ Create or add Project…</option>
          </>
        )}
      </select>
    </span>
  );

  const actions = project && online && <PanelControls layout={layout} />;

  return (
    <div className="page">
      <Header user={user} onLogout={onLogout} context={context} actions={actions} />
      {machines && !machine && <p className="error content">Machine not found.</p>}
      {machine && !online && (
        <p className="content muted">
          {machine.name} is offline. Start it with <code>racler agent start</code>.
        </p>
      )}
      {error && <p className="error content">{error}</p>}
      {online && projects && !project && (
        <ProjectPicker
          machineId={machineId}
          projects={projects}
          workspaceName={workspaceName}
          selectedWorkspaceId={activeWorkspaceId}
        />
      )}
      {online && project && (
        <ProjectIde
          key={`${machineId}/${project.id}`}
          machineId={machineId}
          project={project}
          layout={layout}
          chatId={chatId}
        />
      )}
    </div>
  );
}

function ProjectPicker({
  machineId,
  projects,
  workspaceName,
  selectedWorkspaceId,
}: {
  machineId: string;
  projects: Project[];
  workspaceName: (id: string) => string | undefined;
  selectedWorkspaceId?: string | null;
}) {
  const visible = selectedWorkspaceId
    ? projects.filter((p) => p.workspaceId === selectedWorkspaceId)
    : projects;

  return (
    <main className="content">
      <h2>Projects</h2>
      {visible.length === 0 && (
        <p className="muted">
          {selectedWorkspaceId
            ? "No Projects in this Workspace yet. Create one, or add an existing folder from an approved Workspace."
            : "No Projects on this machine yet. Create one, or add an existing folder from an approved Workspace."}
        </p>
      )}
      <p>
        <button
          className="primary"
          onClick={() =>
            navigate(
              selectedWorkspaceId
                ? `/settings/projects?add=${machineId}&workspace=${selectedWorkspaceId}`
                : `/settings/projects?add=${machineId}`,
            )
          }
          data-testid="picker-add-project"
        >
          Create or add Project
        </button>
      </p>
      <ul className="list">
        {visible.map((p) => (
          <li key={p.id}>
            <button
              className="list-row"
              onClick={() => navigate(projectPath(machineId, p.id))}
              data-testid={`project-${p.name}`}
            >
              <span className="row-main">{p.name}</span>
              <span className="muted small">{workspaceName(p.workspaceId) ?? ""}</span>
              <span className="muted small">{p.location}</span>
            </button>
          </li>
        ))}
      </ul>
    </main>
  );
}

function ProjectIde(props: {
  machineId: string;
  project: Project;
  layout: ReturnType<typeof useIdeLayout>;
  chatId?: string | undefined;
}) {
  const files = useOpenFiles(props.machineId, props.project.id);
  const lsp = useProjectLsp(props.machineId, props.project.id, props.project.name, files);
  const { dirtyPaths } = files;
  const here = props.project.id;
  const machine = props.machineId;
  // Leaving this Project (to another one, Settings, Home…) unmounts the editor: never discard unsaved edits silently.
  useEffect(
    () =>
      setNavigationGuard((target) => {
        const dirty = dirtyPaths().length;
        if (!shouldConfirmLeave({ machineId: machine, projectId: here }, target, dirty)) return true;
        return confirm(
          `You have unsaved changes in ${dirty} open ${dirty === 1 ? "file" : "files"}. Leave this Project and discard them?`,
        );
      }),
    [dirtyPaths, here, machine],
  );
  return (
    <LspProvider value={lsp}>
      <Ide {...props} files={files} />
    </LspProvider>
  );
}
